import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  MessageSquare,
  Plus,
  Search,
  ThumbsUp,
  Heart,
  Lightbulb,
  CheckCircle,
  Brain,
  Eye,
  MessageCircle,
  Award,
  Filter,
  TrendingUp,
  Clock,
  HelpCircle,
  Edit,
  Trash2,
  MoreVertical,
  Home,
  ArrowLeft
} from 'lucide-react';

const CommunityForum = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();
  const { postId } = useParams();
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  // States
  const [posts, setPosts] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [myReactions, setMyReactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [myProfile, setMyProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [deletingPost, setDeletingPost] = useState(null);
  const [editAnswerDialogOpen, setEditAnswerDialogOpen] = useState(false);
  const [deleteAnswerDialogOpen, setDeleteAnswerDialogOpen] = useState(false);
  const [editingAnswer, setEditingAnswer] = useState(null);
  const [deletingAnswer, setDeletingAnswer] = useState(null);
  const [editAnswerContent, setEditAnswerContent] = useState('');
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostCategory, setNewPostCategory] = useState('general');
  const [newAnswerContent, setNewAnswerContent] = useState('');
  
  useEffect(() => {
    if (user?.id) {
      fetchMyProfile();
      fetchCategories();
      fetchMyReactions();
    }
  }, [user]);
  
  // Re-fetch categories when language changes to update translations
  useEffect(() => {
    if (categories.length > 0) {
      fetchCategories();
    }
  }, [t]);
  
  useEffect(() => {
    fetchPosts();
  }, [selectedCategory, sortBy, searchQuery]);
  
  useEffect(() => {
    if (postId) {
      fetchPostDetail(postId);
    }
  }, [postId]);
  
  const fetchMyProfile = async () => {
    try {
      setProfileLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/researchers/user/${user.id}`);
      if (response.ok) {
        const data = await response.json();
        console.log('✅ My Profile Loaded:', data);
        setMyProfile(data);
      } else {
        console.error('❌ Failed to load profile:', response.status);
        setMyProfile(null);
      }
    } catch (error) {
      console.error('❌ Error fetching profile:', error);
      setMyProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };
  
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/categories`);
      if (response.ok) {
        const data = await response.json();
        // Translate category labels
        const translatedCategories = data.categories.map(cat => ({
          ...cat,
          label: getCategoryLabel(cat.value)
        }));
        setCategories(translatedCategories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };
  
  // Helper function to get translated category label
  const getCategoryLabel = (value) => {
    const categoryMap = {
      'all': t('allTopics'),
      'general': t('generalDiscussion'),
      'methods': t('researchMethods'),
      'data_analysis': t('dataAnalysis'),
      'literature': t('literatureReview'),
      'career': t('careerAcademia'),
      'tools': t('toolsSoftware'),
      'collaboration': t('collaborationOpportunities'),
      'feedback': t('feedbackReview')
    };
    return categoryMap[value] || value;
  };
  
  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchQuery) params.append('search', searchQuery);
      params.append('sort_by', sortBy);
      
      const response = await fetch(`${BACKEND_URL}/api/forum/posts?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchPostDetail = async (id) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${id}`);
      if (response.ok) {
        const data = await response.json();
        setSelectedPost(data);
        fetchAnswers(id);
      }
    } catch (error) {
      console.error('Error fetching post detail:', error);
    }
  };
  
  const fetchAnswers = async (postId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/answers/${postId}`);
      if (response.ok) {
        const data = await response.json();
        setAnswers(data);
      }
    } catch (error) {
      console.error('Error fetching answers:', error);
    }
  };
  
  const fetchMyReactions = async () => {
    if (!myProfile?.id) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/my-reactions/${myProfile.id}`);
      if (response.ok) {
        const data = await response.json();
        setMyReactions(data);
      }
    } catch (error) {
      console.error('Error fetching reactions:', error);
    }
  };
  
  const handleCreatePost = async () => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      toast({
        title: t('error'),
        description: t('fillAllFields'),
        variant: "destructive"
      });
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          researcher_id: myProfile.id,
          researcher_name: myProfile.name,
          researcher_avatar: myProfile.profileImage,
          title: newPostTitle,
          content: newPostContent,
          category: newPostCategory,
          tags: []
        })
      });
      
      if (response.ok) {
        toast({
          title: t('success'),
          description: t('postCreatedSuccess')
        });
        setCreateDialogOpen(false);
        setNewPostTitle('');
        setNewPostContent('');
        fetchPosts();
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToCreatePost'),
        variant: "destructive"
      });
    }
  };
  
  const handleCreateAnswer = async () => {
    if (!newAnswerContent.trim()) {
      toast({
        title: t('error'),
        description: t('enterAnswer'),
        variant: "destructive"
      });
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          post_id: selectedPost.id,
          researcher_id: myProfile.id,
          researcher_name: myProfile.name,
          researcher_avatar: myProfile.profileImage,
          content: newAnswerContent
        })
      });
      
      if (response.ok) {
        toast({
          title: t('success'),
          description: t('answerPostedSuccess')
        });
        setNewAnswerContent('');
        fetchAnswers(selectedPost.id);
        fetchPostDetail(selectedPost.id);
      }
    } catch (error) {
      toast({
        title: t('error'),
        description: t('failedToPostAnswer'),
        variant: "destructive"
      });
    }
  };
  
  const handleReaction = async (targetId, targetType, reactionType) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/forum/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_id: targetId,
          target_type: targetType,
          researcher_id: myProfile.id,
          reaction_type: reactionType
        })
      });
      
      if (response.ok) {
        if (selectedPost) {
          fetchPostDetail(selectedPost.id);
        }
        fetchPosts();
        fetchMyReactions();
      }
    } catch (error) {
      console.error('Error toggling reaction:', error);
    }
  };
  
  const handleEditPost = async () => {
    console.log('🚀 Starting Edit Post Handler');
    console.log('📝 Edit Data:', {
      editingPost,
      title: newPostTitle,
      content: newPostContent,
      category: newPostCategory,
      researcherId: myProfile?.id
    });
    
    if (!editingPost || !newPostTitle.trim() || !newPostContent.trim()) {
      console.error('❌ Validation Failed: Missing required fields');
      toast({
        title: t('error'),
        description: t('fillAllFields'),
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log(`📡 Sending PUT request to: ${BACKEND_URL}/api/forum/posts/${editingPost.id}`);
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${editingPost.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newPostTitle,
          content: newPostContent,
          category: newPostCategory,
          researcher_id: myProfile.id
        })
      });
      
      console.log('📥 Response Status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Post Updated Successfully:', data);
        toast({
          title: t('success'),
          description: t('postUpdatedSuccess')
        });
        setEditDialogOpen(false);
        setEditingPost(null);
        setNewPostTitle('');
        setNewPostContent('');
        if (selectedPost) {
          fetchPostDetail(selectedPost.id);
        } else {
          fetchPosts();
        }
      } else {
        const errorData = await response.json();
        console.error('❌ Update Failed:', errorData);
        toast({
          title: t('error'),
          description: errorData.detail || t('failedToUpdatePost'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ Exception in handleEditPost:', error);
      toast({
        title: t('error'),
        description: t('failedToUpdatePost'),
        variant: "destructive"
      });
    }
  };
  
  const handleDeletePost = async () => {
    console.log('🚀 Starting Delete Post Handler');
    console.log('🗑️ Deleting Post:', deletingPost);
    
    if (!deletingPost) return;
    
    try {
      console.log(`📡 Sending DELETE request to: ${BACKEND_URL}/api/forum/posts/${deletingPost.id}`);
      const response = await fetch(`${BACKEND_URL}/api/forum/posts/${deletingPost.id}`, {
        method: 'DELETE'
      });
      
      console.log('📥 Response Status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Post Deleted Successfully:', data);
        toast({
          title: t('success'),
          description: t('postDeletedSuccess')
        });
        setDeleteDialogOpen(false);
        setDeletingPost(null);
        if (selectedPost) {
          setSelectedPost(null);
          navigate('/forum');
        }
        fetchPosts();
      } else {
        const errorData = await response.json();
        console.error('❌ Delete Failed:', errorData);
        toast({
          title: t('error'),
          description: errorData.detail || t('failedToDeletePost'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ Exception in handleDeletePost:', error);
      toast({
        title: t('error'),
        description: t('failedToDeletePost'),
        variant: "destructive"
      });
    }
  };
  
  const openEditDialog = (post) => {
    console.log('🖊️ Opening Edit Dialog for Post:', post);
    setEditingPost(post);
    setNewPostTitle(post.title);
    setNewPostContent(post.content);
    setNewPostCategory(post.category);
    setEditDialogOpen(true);
    console.log('✅ Edit Dialog State Set to Open');
  };
  
  const openDeleteDialog = (post) => {
    console.log('🗑️ Opening Delete Dialog for Post:', post);
    setDeletingPost(post);
    setDeleteDialogOpen(true);
    console.log('✅ Delete Dialog State Set to Open');
  };
  
  const handleEditAnswer = async () => {
    console.log('🚀 Starting Edit Answer Handler');
    console.log('📝 Edit Answer Data:', {
      editingAnswer,
      content: editAnswerContent,
      researcherId: myProfile?.id
    });
    
    if (!editingAnswer || !editAnswerContent.trim()) {
      console.error('❌ Validation Failed: Missing answer content');
      toast({
        title: t('error'),
        description: t('fillAllFields'),
        variant: "destructive"
      });
      return;
    }
    
    try {
      console.log(`📡 Sending PUT request to: ${BACKEND_URL}/api/forum/answers/${editingAnswer.id}`);
      const response = await fetch(`${BACKEND_URL}/api/forum/answers/${editingAnswer.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: editAnswerContent,
          researcher_id: myProfile.id
        })
      });
      
      console.log('📥 Response Status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Answer Updated Successfully:', data);
        toast({
          title: t('success'),
          description: t('answerUpdatedSuccess')
        });
        setEditAnswerDialogOpen(false);
        setEditingAnswer(null);
        setEditAnswerContent('');
        if (selectedPost) {
          fetchPostDetail(selectedPost.id);
        }
      } else {
        const errorData = await response.json();
        console.error('❌ Update Failed:', errorData);
        toast({
          title: t('error'),
          description: errorData.detail || t('failedToUpdateAnswer'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ Exception in handleEditAnswer:', error);
      toast({
        title: t('error'),
        description: t('failedToUpdateAnswer'),
        variant: "destructive"
      });
    }
  };
  
  const handleDeleteAnswer = async () => {
    console.log('🚀 Starting Delete Answer Handler');
    console.log('🗑️ Deleting Answer:', deletingAnswer);
    
    if (!deletingAnswer) return;
    
    try {
      console.log(`📡 Sending DELETE request to: ${BACKEND_URL}/api/forum/answers/${deletingAnswer.id}`);
      const response = await fetch(`${BACKEND_URL}/api/forum/answers/${deletingAnswer.id}`, {
        method: 'DELETE'
      });
      
      console.log('📥 Response Status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Answer Deleted Successfully:', data);
        toast({
          title: t('success'),
          description: t('answerDeletedSuccess')
        });
        setDeleteAnswerDialogOpen(false);
        setDeletingAnswer(null);
        if (selectedPost) {
          fetchPostDetail(selectedPost.id);
        }
      } else {
        const errorData = await response.json();
        console.error('❌ Delete Failed:', errorData);
        toast({
          title: t('error'),
          description: errorData.detail || t('failedToDeleteAnswer'),
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('❌ Exception in handleDeleteAnswer:', error);
      toast({
        title: t('error'),
        description: t('failedToDeleteAnswer'),
        variant: "destructive"
      });
    }
  };
  
  const openEditAnswerDialog = (answer) => {
    console.log('🖊️ Opening Edit Answer Dialog:', answer);
    setEditingAnswer(answer);
    setEditAnswerContent(answer.content);
    setEditAnswerDialogOpen(true);
    console.log('✅ Edit Answer Dialog State Set to Open');
  };
  
  const openDeleteAnswerDialog = (answer) => {
    console.log('🗑️ Opening Delete Answer Dialog:', answer);
    setDeletingAnswer(answer);
    setDeleteAnswerDialogOpen(true);
    console.log('✅ Delete Answer Dialog State Set to Open');
  };
  
  const hasReacted = (targetId, reactionType) => {
    return myReactions.some(r => 
      r.target_id === targetId && r.reaction_type === reactionType
    );
  };
  
  const ReactionButton = ({ targetId, targetType, reactionType, icon: Icon, count, label }) => {
    const reacted = hasReacted(targetId, reactionType);
    return (
      <Button
        variant={reacted ? "default" : "outline"}
        size="sm"
        onClick={() => handleReaction(targetId, targetType, reactionType)}
        className={`gap-1 ${reacted ? 'bg-cyan-600 hover:bg-cyan-700' : 'border-cyan-300 text-cyan-700 hover:bg-cyan-50'}`}
      >
        <Icon className="h-4 w-4" />
        <span>{count || 0}</span>
      </Button>
    );
  };
  
  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <MessageSquare className="h-12 w-12 text-cyan-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-center mb-2">{t('joinCommunityForum')}</h2>
            <p className="text-gray-600 text-center mb-4">
              {t('signInToAccessForum')}
            </p>
            <Button onClick={() => navigate('/')} className="w-full bg-cyan-600 hover:bg-cyan-700">
              {t('signIn')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (profileLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('loadingForum')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (!myProfile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6">
            <MessageSquare className="h-12 w-12 text-cyan-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-center mb-2">{t('researcherProfileRequired')}</h2>
            <p className="text-gray-600 text-center mb-4">
              {t('needProfileForForum')}
            </p>
            <Button onClick={() => navigate('/')} className="w-full bg-cyan-600 hover:bg-cyan-700">
              {t('createResearcherProfile')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Post detail view
  if (selectedPost) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex gap-3 mb-4">
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            >
              <Home className="h-4 w-4 mr-2" />
              {t('home')}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setSelectedPost(null);
                navigate('/forum');
              }}
              className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('backToForum')}
            </Button>
          </div>
          
          {/* Post Detail */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <Badge className="mb-2 bg-cyan-100 text-cyan-800">{getCategoryLabel(selectedPost.category)}</Badge>
                  <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    {selectedPost.title}
                  </h1>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{selectedPost.researcher_name}</span>
                    <span>•</span>
                    <span>{new Date(selectedPost.created_at).toLocaleDateString()}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      {selectedPost.views} {t('views')}
                    </span>
                  </div>
                </div>
                {(() => {
                  console.log('🔍 Post Ownership Check:', {
                    myProfile: myProfile,
                    myProfileId: myProfile?.id,
                    postResearcherId: selectedPost.researcher_id,
                    isOwner: myProfile && myProfile.id === selectedPost.researcher_id
                  });
                  return myProfile && myProfile.id === selectedPost.researcher_id;
                })() && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditDialog(selectedPost)}>
                        <Edit className="h-4 w-4 mr-2" />
                        {t('editPost')}
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => openDeleteDialog(selectedPost)}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        {t('deletePost')}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 whitespace-pre-wrap mb-4">
                {selectedPost.content}
              </p>
              
              <div className="flex gap-2 flex-wrap">
                <ReactionButton
                  targetId={selectedPost.id}
                  targetType="post"
                  reactionType="like"
                  icon={ThumbsUp}
                  count={selectedPost.reaction_counts?.like}
                  label="Like"
                />
                <ReactionButton
                  targetId={selectedPost.id}
                  targetType="post"
                  reactionType="love"
                  icon={Heart}
                  count={selectedPost.reaction_counts?.love}
                  label="Love"
                />
                <ReactionButton
                  targetId={selectedPost.id}
                  targetType="post"
                  reactionType="insightful"
                  icon={Lightbulb}
                  count={selectedPost.reaction_counts?.insightful}
                  label="Insightful"
                />
                <ReactionButton
                  targetId={selectedPost.id}
                  targetType="post"
                  reactionType="helpful"
                  icon={CheckCircle}
                  count={selectedPost.reaction_counts?.helpful}
                  label="Helpful"
                />
                <ReactionButton
                  targetId={selectedPost.id}
                  targetType="post"
                  reactionType="thinking"
                  icon={Brain}
                  count={selectedPost.reaction_counts?.thinking}
                  label="Thinking"
                />
              </div>
            </CardContent>
          </Card>
          
          {/* Answers Section */}
          <Card>
            <CardHeader>
              <h2 className="text-xl font-bold">
                {answers.length} {answers.length === 1 ? t('answer') : t('answers')}
              </h2>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {answers.map((answer) => (
                  <div
                    key={answer.id}
                    className={`p-4 rounded-lg ${
                      answer.is_best_answer ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50'
                    }`}
                  >
                    {answer.is_best_answer && (
                      <div className="flex items-center gap-2 text-green-700 font-semibold mb-2">
                        <Award className="h-5 w-5" />
                        {t('bestAnswer')}
                      </div>
                    )}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className="font-medium text-gray-900">
                          {answer.researcher_name}
                        </span>
                        <span className="text-sm text-gray-500 ml-2">
                          {new Date(answer.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {(() => {
                        console.log('🔍 Answer Ownership Check:', {
                          answerId: answer.id,
                          myProfile: myProfile,
                          myProfileId: myProfile?.id,
                          answerResearcherId: answer.researcher_id,
                          isOwner: myProfile && myProfile.id === answer.researcher_id
                        });
                        return myProfile && myProfile.id === answer.researcher_id;
                      })() && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditAnswerDialog(answer)}>
                              <Edit className="h-4 w-4 mr-2" />
                              {t('editAnswer')}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => openDeleteAnswerDialog(answer)}
                              className="text-red-600"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              {t('deleteAnswer')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap mb-3">
                      {answer.content}
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <ReactionButton
                        targetId={answer.id}
                        targetType="answer"
                        reactionType="like"
                        icon={ThumbsUp}
                        count={answer.reaction_counts?.like}
                      />
                      <ReactionButton
                        targetId={answer.id}
                        targetType="answer"
                        reactionType="helpful"
                        icon={CheckCircle}
                        count={answer.reaction_counts?.helpful}
                      />
                      <ReactionButton
                        targetId={answer.id}
                        targetType="answer"
                        reactionType="insightful"
                        icon={Lightbulb}
                        count={answer.reaction_counts?.insightful}
                      />
                    </div>
                  </div>
                ))}
                
                {/* Answer Form */}
                <div className="border-t pt-6">
                  <h3 className="font-semibold mb-3">{t('yourAnswer')}</h3>
                  <Textarea
                    placeholder={t('shareYourKnowledge')}
                    value={newAnswerContent}
                    onChange={(e) => setNewAnswerContent(e.target.value)}
                    rows={5}
                    className="mb-3"
                  />
                  <Button onClick={handleCreateAnswer} className="bg-cyan-600 hover:bg-cyan-700">
                    {t('postAnswer')}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Edit Post Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('editPost')}</DialogTitle>
              <DialogDescription>
                {t('updateYourQuestion')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t('title')}</label>
                <Input
                  placeholder={t('whatsYourQuestion')}
                  value={newPostTitle}
                  onChange={(e) => setNewPostTitle(e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">{t('category')}</label>
                <Select value={newPostCategory} onValueChange={setNewPostCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.filter(c => c.value !== 'all').map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="text-sm font-medium mb-2 block">{t('details')}</label>
                <Textarea
                  placeholder={t('provideMoreDetails')}
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  rows={8}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button onClick={handleEditPost} className="bg-cyan-600 hover:bg-cyan-700">
                {t('updatePost')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('deletePost')}</DialogTitle>
              <DialogDescription>
                {t('deleteConfirmPost')}
              </DialogDescription>
            </DialogHeader>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleDeletePost} 
                className="bg-red-600 hover:bg-red-700"
              >
                {t('deletePost')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Edit Answer Dialog */}
        <Dialog open={editAnswerDialogOpen} onOpenChange={setEditAnswerDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{t('editAnswer')}</DialogTitle>
              <DialogDescription>
                {t('updateYourAnswer')}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">{t('answer')}</label>
                <Textarea
                  placeholder={t('shareKnowledgePlaceholder')}
                  value={editAnswerContent}
                  onChange={(e) => setEditAnswerContent(e.target.value)}
                  rows={8}
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditAnswerDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button onClick={handleEditAnswer} className="bg-cyan-600 hover:bg-cyan-700">
                {t('updateAnswer')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Answer Confirmation Dialog */}
        <Dialog open={deleteAnswerDialogOpen} onOpenChange={setDeleteAnswerDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('deleteAnswer')}</DialogTitle>
              <DialogDescription>
                {t('deleteConfirmAnswer')}
              </DialogDescription>
            </DialogHeader>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteAnswerDialogOpen(false)}>
                {t('cancel')}
              </Button>
              <Button 
                onClick={handleDeleteAnswer} 
                className="bg-red-600 hover:bg-red-700"
              >
                {t('deleteAnswer')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }
  
  // Forum listing view
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Back to Home Button */}
        <div className="mb-4">
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('backToHome')}
          </Button>
        </div>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{t('communityForum')}</h1>
            <p className="text-gray-600">{t('connectShareCollaborate')}</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2 bg-cyan-600 hover:bg-cyan-700">
            <Plus className="h-4 w-4" />
            {t('askQuestion')}
          </Button>
        </div>
        
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder={t('searchQuestions')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger>
                  <SelectValue placeholder={t('category')} />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger>
                  <SelectValue placeholder={t('sortBy')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">{t('newestFirst')}</SelectItem>
                  <SelectItem value="oldest">{t('oldestFirst')}</SelectItem>
                  <SelectItem value="popular">{t('mostPopular')}</SelectItem>
                  <SelectItem value="unanswered">{t('unanswered')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
        
        {/* Posts List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div>
          </div>
        ) : posts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">{t('noQuestionsFound')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map((post) => (
              <Card
                key={post.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => {
                  setSelectedPost(post);
                  navigate(`/forum/${post.id}`);
                }}
              >
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="flex flex-col items-center gap-2 text-sm text-gray-500">
                      <div className="flex items-center gap-1">
                        <MessageCircle className="h-4 w-4" />
                        <span>{post.answer_count}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="h-4 w-4" />
                        <span>{post.views}</span>
                      </div>
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <Badge className="mb-2">{getCategoryLabel(post.category)}</Badge>
                          <h3 className="text-lg font-semibold text-gray-900 hover:text-cyan-600">
                            {post.title}
                          </h3>
                        </div>
                        {post.is_answered && (
                          <Badge variant="success" className="bg-green-100 text-green-800">
                            ✓ {t('answered')}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-gray-600 line-clamp-2 mb-3">
                        {post.content}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>{post.researcher_name}</span>
                          <span>•</span>
                          <span>{new Date(post.created_at).toLocaleDateString()}</span>
                        </div>
                        
                        <div className="flex gap-3 text-sm">
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="h-4 w-4" />
                            {post.reaction_counts?.like || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Heart className="h-4 w-4" />
                            {post.reaction_counts?.love || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Lightbulb className="h-4 w-4" />
                            {post.reaction_counts?.insightful || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
      
      {/* Create Post Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('askQuestionTitle')}</DialogTitle>
            <DialogDescription>
              {t('shareQuestionWithCommunity')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('title')}</label>
              <Input
                placeholder={t('whatsYourQuestion')}
                value={newPostTitle}
                onChange={(e) => setNewPostTitle(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">{t('category')}</label>
              <Select value={newPostCategory} onValueChange={setNewPostCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.value !== 'all').map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">{t('details')}</label>
              <Textarea
                placeholder={t('provideMoreDetails')}
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                rows={8}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleCreatePost} className="bg-cyan-600 hover:bg-cyan-700">
              {t('postQuestion')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Post Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('editPost')}</DialogTitle>
            <DialogDescription>
              {t('updateYourQuestion')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('title')}</label>
              <Input
                placeholder={t('whatsYourQuestion')}
                value={newPostTitle}
                onChange={(e) => setNewPostTitle(e.target.value)}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">{t('category')}</label>
              <Select value={newPostCategory} onValueChange={setNewPostCategory}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.filter(c => c.value !== 'all').map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium mb-2 block">{t('details')}</label>
              <Textarea
                placeholder={t('provideMoreDetails')}
                value={newPostContent}
                onChange={(e) => setNewPostContent(e.target.value)}
                rows={8}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleEditPost} className="bg-cyan-600 hover:bg-cyan-700">
              {t('updatePost')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deletePost')}</DialogTitle>
            <DialogDescription>
              {t('deleteConfirmPost')}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleDeletePost} 
              className="bg-red-600 hover:bg-red-700"
            >
              {t('deletePost')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Answer Dialog */}
      <Dialog open={editAnswerDialogOpen} onOpenChange={setEditAnswerDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('editAnswer')}</DialogTitle>
            <DialogDescription>
              {t('updateYourAnswer')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium mb-2 block">{t('answer')}</label>
              <Textarea
                placeholder={t('shareKnowledgePlaceholder')}
                value={editAnswerContent}
                onChange={(e) => setEditAnswerContent(e.target.value)}
                rows={8}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAnswerDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button onClick={handleEditAnswer} className="bg-cyan-600 hover:bg-cyan-700">
              {t('updateAnswer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Answer Confirmation Dialog */}
      <Dialog open={deleteAnswerDialogOpen} onOpenChange={setDeleteAnswerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('deleteAnswer')}</DialogTitle>
            <DialogDescription>
              {t('deleteConfirmAnswer')}
            </DialogDescription>
          </DialogHeader>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteAnswerDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleDeleteAnswer} 
              className="bg-red-600 hover:bg-red-700"
            >
              {t('deleteAnswer')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityForum;
